// CONTRACT:
// PURPOSE: [Клиентский компонент пагинации для страницы рейтинга]
// SCOPE: [rating-pagination, searchParams]

"use client";

import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { useCallback, useTransition } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface RatingPaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  perPage: number;
}

export function RatingPagination({
  currentPage,
  totalPages,
  totalCount,
  perPage,
}: RatingPaginationProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();

  const goToPage = useCallback(
    (page: number) => {
      const params = new URLSearchParams(searchParams.toString());
      if (page > 1) {
        params.set("page", String(page));
      } else {
        params.delete("page");
      }
      startTransition(() => {
        router.push(`${pathname}?${params.toString()}`);
      });
    },
    [searchParams, pathname, router]
  );

  const from = (currentPage - 1) * perPage + 1;
  const to = Math.min(currentPage * perPage, totalCount);

  // Генерируем номера страниц (макс 7 видимых)
  const getPageNumbers = () => {
    const pages: (number | "...")[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <div className="py-8">
      <div className="flex flex-col items-center gap-4">
        {/* Навигация */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1 || isPending}
            className="p-2 text-white/40 hover:text-white hover:bg-white/5 disabled:opacity-20 disabled:cursor-not-allowed transition-colors rounded-sm"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          {getPageNumbers().map((page, i) =>
            page === "..." ? (
              <span
                key={`dots-${i}`}
                className="px-2 py-1 text-xs text-white/20"
              >
                …
              </span>
            ) : (
              <button
                key={page}
                onClick={() => goToPage(page)}
                disabled={isPending}
                className={`min-w-[32px] h-8 text-xs font-bold uppercase tracking-wider transition-colors rounded-sm ${
                  page === currentPage
                    ? "bg-white text-black"
                    : "text-white/40 hover:bg-white/5 hover:text-white"
                }`}
              >
                {page}
              </button>
            )
          )}

          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= totalPages || isPending}
            className="p-2 text-white/40 hover:text-white hover:bg-white/5 disabled:opacity-20 disabled:cursor-not-allowed transition-colors rounded-sm"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Счётчик */}
        <p className="text-[10px] text-white/20 uppercase tracking-wider">
          Показано {from}–{to} из {totalCount.toLocaleString("ru-RU")} ЧОПов
        </p>
      </div>
    </div>
  );
}
